import { Badge, Box, Button, Group, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/table-core';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountsTable, {
  AccountsTableProps,
} from '../accounts-table/accounts-table';
import Modal from '../modal/modal';

import classes from './accounts-selector.module.css';

import { accountsAtom } from '@data-access';

type RenderBottomToolbarCustomActions = NonNullable<
  NonNullable<AccountsTableProps['options']>['renderBottomToolbarCustomActions']
>;

export function AccountsSelector() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [opened, actions] = useDisclosure();
  const [accounts, setAccounts] = useAtom(accountsAtom);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const renderActions: RenderBottomToolbarCustomActions = useCallback(
    ({ table }) => (
      <Group>
        <Button
          onClick={() => {
            const ids = Object.keys(rowSelection).map((id) => Number(id));
            setAccounts(ids);
            actions.close();
            queryClient.invalidateQueries();
          }}
        >
          {t('common.confirm')}
        </Button>

        <Button color="red" onClick={actions.close}>
          {t('common.cancel')}
        </Button>

        <Button
          onClick={() => {
            setAccounts([]);
            setRowSelection({});
            actions.close();
            queryClient.invalidateQueries();
          }}
          variant="subtle"
        >
          {t('common.reset')}
        </Button>
      </Group>
    ),
    [actions, queryClient, rowSelection, setAccounts, t],
  );

  return (
    <>
      <Button
        color="pf-green"
        onClick={actions.open}
        rightSection={
          <Badge
            color={accounts.length > 0 ? 'orange' : 'pf-green'}
            variant="light"
          >
            {accounts.length}
          </Badge>
        }
        variant="white"
      >
        {t('common.account')}
      </Button>
      <Modal
        classNames={{
          root: clsx(classes.modal),
          content: clsx(classes.modalContent),
          header: clsx(classes.modalHeader),
          title: clsx(classes.modalTitle),
          body: clsx(classes.modalBody),
        }}
        onClose={actions.close}
        opened={opened}
        size="80%"
        title={t('common.search_account')}
      >
        <Box maw="100%">
          <AccountsTable
            options={{
              enableFullScreenToggle: false,

              renderBottomToolbarCustomActions: renderActions,
              enableRowSelection: true,
              enableMultiRowSelection: false,
              onRowSelectionChange: setRowSelection,
              initialState: {
                rowSelection: accounts.reduce(
                  (acc, id) => {
                    acc[id] = true;
                    return acc;
                  },
                  {} as Record<number, boolean>,
                ),
              },
            }}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            withActions={false}
          />
        </Box>
      </Modal>
    </>
  );
}

export default AccountsSelector;
